import { io } from 'socket.io-client';

/**
 * WebSocket service for real-time tournament updates
 * Prevents duplicate connections for the same user/tournament
 */
class WebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.messageHandlers = new Map();
    this.connected = false;
    this.authenticated = false;
    this.tournamentId = null;
    this.userId = null;
    this.token = null;
    this.connectionStatusCallbacks = new Set();
    this.isConnecting = false;
    this.connectionPromise = null;
  }

  /**
   * Connect to WebSocket server for tournament updates
   * @param {string} tournamentId - Tournament ID to subscribe to
   * @param {string} token - Authentication token
   * @param {string} userId - User ID (optional, extracted from token if not provided)
   */
  async connect(tournamentId, token, userId = null) {
    // If already connecting, wait for the existing connection
    if (this.isConnecting && this.connectionPromise) {
      console.log('🔄 WebSocket connection already in progress, waiting...');
      return this.connectionPromise;
    }

    // If already connected to the same tournament with the same user, return
    if (this.socket && this.socket.connected && 
        this.tournamentId === tournamentId && 
        this.userId === userId) {
      console.log('✅ WebSocket already connected to the same tournament and user');
      return Promise.resolve();
    }

    // If connected to a different tournament or user, disconnect first
    if (this.socket && this.socket.connected && 
        (this.tournamentId !== tournamentId || this.userId !== userId)) {
      console.log('🔄 Switching tournament/user, disconnecting previous connection');
      this.disconnect();
    }

    this.isConnecting = true;
    this.connectionPromise = this._performConnection(tournamentId, token, userId);
    
    try {
      await this.connectionPromise;
    } finally {
      this.isConnecting = false;
      this.connectionPromise = null;
    }
  }

  /**
   * Perform the actual connection
   * @private
   */
  async _performConnection(tournamentId, token, userId) {
    this.tournamentId = tournamentId;
    this.token = token;
    this.userId = userId;

    console.log('🔍 Environment Variables Debug in websocketService:', {
      REACT_APP_WS_URL: process.env.REACT_APP_WS_URL,
      REACT_APP_API_URL: process.env.REACT_APP_API_URL,
      NODE_ENV: process.env.NODE_ENV
    });

    const serverUrl = process.env.REACT_APP_WS_URL || 'http://localhost:9890';
    console.log('🔌 Final serverUrl being used:', serverUrl);
    
    console.log(`🔌 Connecting to Socket.IO server: ${serverUrl}`);
    console.log(`🔑 Using token: ${token ? 'Present' : 'Missing'}`);
    console.log(`🏆 Tournament ID: ${tournamentId}`);
    console.log(`👤 User ID: ${userId || 'Not provided'}`);
    
    return new Promise((resolve, reject) => {
      try {
        this.socket = io(serverUrl, {
          auth: {
            token: token
          },
          transports: ['websocket', 'polling'],
          timeout: 20000,
          forceNew: true
        });
        
        this.setupEventHandlers(resolve, reject);
      } catch (error) {
        console.error('Socket.IO connection error:', error);
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  /**
   * Setup Socket.IO event handlers
   */
  setupEventHandlers(resolve, reject) {
    this.socket.on('connect', () => {
      console.log('✅ Socket.IO connected successfully!');
      console.log('📡 Socket ID:', this.socket.id);
      console.log('👤 User ID:', this.userId);
      console.log('🏆 Tournament ID:', this.tournamentId);
      
      this.connected = true;
      this.authenticated = true;
      this.reconnectAttempts = 0;
      
      // Notify connection status change
      this.notifyConnectionStatusChange();
      
      // Join tournament room
      console.log('🏆 Subscribing to tournament:', this.tournamentId);
      this.socket.emit('subscribe_tournament', {
        tournamentId: this.tournamentId
      });

      if (resolve) resolve();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Socket.IO disconnected:', reason);
      this.connected = false;
      this.authenticated = false;
      
      // Notify connection status change
      this.notifyConnectionStatusChange();
      
      if (reason !== 'io client disconnect') {
        this.handleReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket.IO connection error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        description: error.description,
        context: error.context,
        type: error.type
      });
      this.connected = false;
      this.authenticated = false;
      
      // Check if it's an authentication error
      if (error.message && error.message.includes('Authentication')) {
        console.error('🔐 WebSocket authentication failed:', error.message);
      }
      
      // Notify connection status change
      this.notifyConnectionStatusChange();
      
      if (reject) reject(error);
    });

    // Handle tournament-specific events
    this.socket.on('BET_PLACED', (data) => {
      console.log('🎯 BET_PLACED EVENT RECEIVED!', data);
      this.handleMessage(data);
    });

    this.socket.on('WALLET_BALANCE_UPDATED', (data) => {
      console.log('💰 WALLET_BALANCE_UPDATED EVENT RECEIVED!', data);
      this.handleMessage(data);
    });

    this.socket.on('PLAYER_AVAILABILITY_UPDATED', (data) => {
      console.log('👤 PLAYER_AVAILABILITY_UPDATED EVENT RECEIVED!', data);
      this.handleMessage(data);
    });

    this.socket.on('PURCHASE_CONFIRMED', (data) => {
      console.log('✅ PURCHASE_CONFIRMED EVENT RECEIVED!', data);
      this.handleMessage(data);
    });

    this.socket.on('TOURNAMENT_INFO_UPDATED', (data) => {
      console.log('🏆 TOURNAMENT_INFO_UPDATED EVENT RECEIVED!', data);
      this.handleMessage(data);
    });

    this.socket.on('LEADERBOARD_UPDATED', (data) => {
      console.log('📊 LEADERBOARD_UPDATED EVENT RECEIVED!', data);
      this.handleMessage(data);
    });

    this.socket.on('score_update', (data) => {
      console.log('📨 Score update received:', data);
      this.handleMessage(data);
    });

    this.socket.on('tournament_subscribed', (data) => {
      console.log('📨 Tournament subscribed received:', data);
      this.handleMessage(data);
    });

    this.socket.on('connected', (data) => {
      console.log('📨 Connected message received:', data);
    });

    this.socket.on('error', (data) => {
      console.error('📨 Error message received:', data);
    });

    this.socket.on('duplicate_connection', (data) => {
      console.log('🔄 Duplicate connection detected, closing old connection:', data);
      // The old connection will be closed by the server
    });
  }

  /**
   * Handle incoming WebSocket messages
   * @param {Object} data - Parsed message data
   */
  handleMessage(data) {
    // Extract type from nested data structure (data.data.type)
    const type = data.data?.type || data.type;
    console.log('🚀 HANDLEMESSAGE CALLED!', { type, data });
    console.log('🔍 handleMessage called with:', { type, data });
    
    // Call registered handlers for this message type
    const handlers = this.messageHandlers.get(type) || [];
    console.log(`🔍 Found ${handlers.length} handlers for type: ${type}`);
    handlers.forEach((handler, index) => {
      try {
        console.log(`🔍 Calling handler ${index} for ${type}`);
        handler(data);
      } catch (error) {
        console.error(`Error in message handler for ${type}:`, error);
      }
    });

    // Only log unhandled if no handlers were found
    if (handlers.length === 0) {
      console.log(`🔔 Unhandled message type: ${type}`);
    }
  }

  /**
   * Register a message handler for specific message types
   * @param {string} messageType - Type of message to handle
   * @param {Function} handler - Handler function
   */
  on(messageType, handler) {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, []);
    }
    this.messageHandlers.get(messageType).push(handler);
  }

  /**
   * Remove a message handler
   * @param {string} messageType - Type of message
   * @param {Function} handler - Handler function to remove
   */
  off(messageType, handler) {
    const handlers = this.messageHandlers.get(messageType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Send message to Socket.IO server
   * @param {string} event - Event name
   * @param {Object} data - Data to send
   */
  emit(event, data) {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('Socket.IO not connected, message not sent:', event, data);
    }
  }

  /**
   * Send message to WebSocket server (legacy method for compatibility)
   * @param {Object} message - Message to send
   */
  send(message) {
    if (message.type) {
      this.emit(message.type.toLowerCase(), message);
    } else {
      console.warn('Invalid message format:', message);
    }
  }

  /**
   * Handle reconnection logic
   */
  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = 1000 * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff
    
    console.log(`🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      if (this.tournamentId && this.token) {
        this.connect(this.tournamentId, this.token, this.userId);
      }
    }, delay);
  }

  /**
   * Disconnect from Socket.IO
   */
  disconnect() {
    console.log('🔌 Disconnecting Socket.IO');
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.connected = false;
    this.authenticated = false;
    this.isConnecting = false;
    this.connectionPromise = null;
    this.messageHandlers.clear();
    this.tournamentId = null;
    this.userId = null;
    this.token = null;
    
    // Notify connection status change
    this.notifyConnectionStatusChange();
  }

  /**
   * Switch to a different tournament
   * @param {string} tournamentId - New tournament ID
   */
  async switchTournament(tournamentId) {
    if (this.tournamentId === tournamentId) {
      console.log('✅ Already connected to this tournament');
      return;
    }

    console.log(`🔄 Switching from tournament ${this.tournamentId} to ${tournamentId}`);
    
    if (this.socket && this.socket.connected) {
      // Unsubscribe from current tournament
      this.socket.emit('unsubscribe_tournament', {
        tournamentId: this.tournamentId
      });
    }

    // Connect to new tournament
    await this.connect(tournamentId, this.token, this.userId);
  }

  /**
   * Get current connection info
   * @returns {Object} Connection information
   */
  getConnectionInfo() {
    return {
      connected: this.isConnected(),
      tournamentId: this.tournamentId,
      userId: this.userId,
      socketId: this.socket?.id,
      isConnecting: this.isConnecting
    };
  }

  /**
   * Get connection status
   * @returns {boolean} True if connected
   */
  isConnected() {
    return this.connected && this.authenticated && this.socket && this.socket.connected;
  }

  /**
   * Register a callback for connection status changes
   * @param {Function} callback - Callback function to call when status changes
   */
  onConnectionStatusChange(callback) {
    this.connectionStatusCallbacks.add(callback);
  }

  /**
   * Remove a connection status callback
   * @param {Function} callback - Callback function to remove
   */
  offConnectionStatusChange(callback) {
    this.connectionStatusCallbacks.delete(callback);
  }

  /**
   * Notify all registered callbacks of connection status change
   */
  notifyConnectionStatusChange() {
    const isConnected = this.isConnected();
    this.connectionStatusCallbacks.forEach(callback => {
      try {
        callback(isConnected);
      } catch (error) {
        console.error('Error in connection status callback:', error);
      }
    });
  }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;